import { Color } from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ReactComponent as ImageTopCustomersDKComponent } from '../../../../assets/images/dashboard-empty-state/dk/top-customers.svg'
import { ReactComponent as ImageTopCustomersENComponent } from '../../../../assets/images/dashboard-empty-state/en/top-customers.svg'
import { ReactComponent as ImageTopCustomersFRComponent } from '../../../../assets/images/dashboard-empty-state/fr/top-customers.svg'
import { LocaleCode } from '../../../../enums/localeCode'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useLocale } from '../../../../hooks/useLocale'
import { EmptyStateImage } from '../../elements/EmptyStateImage'
import { Widget } from '../../elements/Widget'
import { useFiscalYearQueryParams } from '../../hooks/useFiscalYearQueryParams'
import { fetchTopCustomers, StatsDataQueryParams } from '../../services/query-api'
import { getUsersWithColors } from '../../utils/getUsersWithColors'
import { UsersList, UsersListContext } from '../UsersList'

const emptyImageByLanguage: Record<LocaleCode, ReactElement> = {
  [LocaleCode.DA]: <ImageTopCustomersDKComponent />,
  [LocaleCode.EN_US]: <ImageTopCustomersENComponent />,
  [LocaleCode.FR]: <ImageTopCustomersFRComponent />,
}

export const TopCustomers = (): ReactElement => {
  const { organization } = useUserOrganization()
  const { queryParams } = useFiscalYearQueryParams(organization?.id)
  const { t } = useTranslation()
  const { code = LocaleCode.EN_US } = useLocale()

  const localeCode = code as LocaleCode

  const { data: topCustomers, isLoading: isTopCustomersLoading } = useQuery(
    [QueryKeys.TopCustomers, organization?.id, queryParams],
    () => fetchTopCustomers(organization?.id || '', undefined, queryParams || ({} as StatsDataQueryParams)),
    {
      enabled: !!organization && !!queryParams,
      refetchOnMount: 'always',
    },
  )

  const colors = useMemo(() => [Color.Green300, Color.Yellow300, Color.Green950], [])
  const topCustomersWithColor = getUsersWithColors(topCustomers?.data || [], colors)
  const isLoading = !organization || isTopCustomersLoading

  const doTopCustomersExist = topCustomersWithColor.length > 0
  const shouldShowEmptyState = !!organization && !doTopCustomersExist && !isLoading

  return (
    <Widget title={t('dashboard.widget.topCustomers.title')}>
      {shouldShowEmptyState && (
        <EmptyStateImage image={emptyImageByLanguage[localeCode] ?? emptyImageByLanguage[LocaleCode.EN_US]} />
      )}
      {doTopCustomersExist && (
        <UsersList usersListContext={UsersListContext.Customer} isLoading={isLoading} users={topCustomersWithColor} />
      )}
    </Widget>
  )
}
